import { Link } from "react-router-dom";
import { LoginAlertProps, LoginFields } from "../../components/SmartErrorAlert";
import { LoginResponse, MfaSendCodeResponse } from "../../hooks/useLoginApi";
import { hasErrorMessage } from "../getApiErrorMessage";
import translate from "../translate";
import { ErrorContentGenerator, ErrorContentMap, InvalidFieldMessages } from "./getAlertContent";

type LoginErrState =
    'tempLocked' |
    'disabled' |
    'incorrectPassword1' |
    'incorrectPassword2' |
    'nonExistentUser' |
    'locked' |
    'unknown';

export const apiErrorContent: ErrorContentMap<LoginErrState> = {
    tempLocked: {
        header: translate('your_account_now_locked_protect_information'),
        body: translate('you_can_try_login_again'),
        analyticsErr: 'Your account is now locked to protect your information. You can try to log in again in 1 hour.'
    },
    disabled: {
        header: translate('your_account_not_active'),
        body: <>
            {translate('to_reactivate_account_call_medicare')} ({<a href="tel:18006334227">1-800-633-4227</a>}).
            {' '}{translate('tty_users_can_call')} {<a href="tel:1-877-486-2048">1-877-486-2048</a>}.
        </>,
        analyticsErr: 'Your account is no longer active. To reactivate your account, call 1-800-MEDICARE (1-800-633-4227). TTY users can call 1-877-486-2048.'
    },
    incorrectPassword1: {
        header: translate("your_username_password_not_match"),
        body: translate('check_information_try_again'),
        analyticsErr: "Your username or password doesn't match our records. Check the information you entered and try again."
    },
    incorrectPassword2: {
        header: translate("your_username_password_not_match"),
        body: translate("you_have_one_more_try_reenter_username"),
        analyticsErr: "Your username or password doesn't match our records. You have 1 more try to re-enter your username and password. If it doesn't match our records, your account will be locked for 1 hour."
    },
    nonExistentUser: {
        header: translate("we_cant_find_you"),
        body: translate('check_information_try_again'),
        analyticsErr: "We can't find you in our records. Check the information you entered and try again."
    },
    locked: {
        header: translate('your_account_now_locked_protect_information'),
        body: <Link to="/unlock-account">{translate('unlock_my_account')}.</Link>,
        analyticsErr: 'Your account is now locked to protect your information. Unlock my account.',
    },
    unknown: {
        header: translate("we_cant_process_request"),
        body: translate('try_logging_into_account_later'),
        analyticsErr: "We can't process your request at this time. Try logging into your account later."
    }
};

const invalidFieldMessages: InvalidFieldMessages<LoginFields> = {
    username: {
        linkText: 'username',
        messages: {
            empty: 'enter_your_usermame_with_period'
        }
    },
    password: {
        linkText: 'password',
        messages: {
            empty: 'enter_your_password'
        }
    }
}

export const isLoginResponse = (res: LoginResponse | MfaSendCodeResponse): res is LoginResponse => {
    // We identify a response as a login response if it has a location property (success)
    // or a string value in its code property (error, SSO-1, MFA_USER_OPTED_OUT)
    return (!!(res as LoginResponse).code && typeof (res as LoginResponse).code === 'string') || !!(res as LoginResponse).location;
}

export const loginErrorFns = new ErrorContentGenerator<LoginAlertProps, LoginFields, LoginErrState, number>({
    apiErrorContent,
    invalidFieldMessages,
    invalidFieldHeader: 'complete_items_log_into_account',
    getRefMap: props => {
        return {
            username: props.usernameRef,
            password: props.passwordRef
        }
    },
    getErrState: (apiResponse, loginFailCount = 0) => {
        if (!apiResponse) {
            return null;
        }
        const responseData = apiResponse.data;
        const isLogin = isLoginResponse(responseData);
        const successCode = isLogin ? 200 : 204;

        if (apiResponse.status === successCode) {
            return null;
        }

        if (!isLogin) {
            // MfaSendCodeResponse
            if (apiResponse.status !== 204) {
                const errors = responseData.errors || [];
                if (hasErrorMessage(errors, 'MFA_USER_OPTED_OUT')) {
                    return null;
                }
                return 'unknown';
            } else {
                return null;
            }
        }

        switch(responseData.code) {
            case 'SSO-0':
                return null; // handled as invalid field errors
            case 'SSO-1':
                return 'tempLocked';
            case 'SSO-2':
                return 'disabled';
            case 'SSO-3':
                if (loginFailCount < 3) {
                    return 'incorrectPassword1';
                } else if (loginFailCount === 3) {
                    return 'incorrectPassword2';
                }
                return 'tempLocked';
            case 'SSO-4':
            case 'SSO-6':
            case 'SSO-13':
                return null; // update account
            case 'SSO-7':
                return 'nonExistentUser';
            case 'SSO-12':
                return 'locked';
            case 'MFA_AUTH_REQUIRED':
                return null; // redirect to OTP confirmation page
            default:
                return 'unknown';
        }
    },
    getApiInvalidFields: apiResponse => {
        const data = apiResponse?.data;
        if (data && isLoginResponse(data) && data.code === 'SSO-0') {
            return [
                { name: 'username', type: 'invalid' },
                { name: 'password', type: 'invalid' }
            ]
        }
        return [];
    },
    isIntermediateStep: false
})